import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiService from '../services/ApiService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearLocalAuthState = useCallback(() => {
    try {
      localStorage.removeItem('user');
    } catch (e) {
      // ignore
    }

    setUser(null);
    setToken(null);
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');

    // Best-effort bootstrap from cached user (non-sensitive)
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setToken('cookie');
      } catch (e) {
        // ignore
      }
    }

    // Always verify cookie session with server (source of truth)
    (async () => {
      try {
        const freshUser = await ApiService.getUser();
        if (freshUser) {
          setUser(freshUser);
          setToken('cookie');
          try {
            localStorage.setItem('user', JSON.stringify(freshUser));
          } catch (e) {
            // ignore
          }
        } else {
          clearLocalAuthState();
        }
      } catch (e) {
        // If cookie session is missing/expired, treat as logged out
        clearLocalAuthState();
      } finally {
        setLoading(false);
      }
    })();
  }, [clearLocalAuthState]);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearLocalAuthState();
      setLoading(false);
    };

    window.addEventListener('auth:expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [clearLocalAuthState]);

  const login = useCallback(async (credentials) => {
    try {
      // Single API call - login endpoint already returns user data + sets httpOnly cookie
      const response = await ApiService.login(credentials);

      const nextUser = response?.user || response?.data?.user || response?.data || response;

      if (!nextUser || !nextUser.id) {
        throw new Error('Login failed to establish a valid session. Please try again.');
      }

      // Save to localStorage
      try {
        localStorage.setItem('user', JSON.stringify(nextUser));
      } catch (e) {
        // ignore
      }

      // Update state - this triggers all consumers to re-render
      setUser(nextUser);
      setToken('cookie');
      setLoading(false);

      return response;
    } catch (error) {
      const normalizedMessage =
        error?.message ||
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        'Login failed. Please try again.';

      clearLocalAuthState();
      throw error instanceof Error ? error : new Error(normalizedMessage);
    }
  }, [clearLocalAuthState]);

  const register = useCallback(async (userData) => {
    try {
      const response = await ApiService.register(userData);
      return response;
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear local state first
    clearLocalAuthState();
    setLoading(false);

    // Dispatch event for any listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }

    try {
      // Call server to clear httpOnly cookie
      await ApiService.logout();
    } catch (error) {
      // Continue with logout even if server call fails
      console.warn('[AuthContext] Server logout failed, but local state cleared');
    }
  }, [clearLocalAuthState]);

  const updateUserPoints = useCallback((newPoints) => {
    setUser(prevUser => {
      const nextPoints = Number(newPoints);
      const updatedUser = { ...prevUser, points: Number.isFinite(nextPoints) ? nextPoints : newPoints };

      try {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (e) {
        // ignore
      }

      try {
        window.dispatchEvent(new CustomEvent('gg:points', { detail: { points: updatedUser.points } }));
      } catch (e) {
        // ignore
      }

      return updatedUser;
    });
  }, []);

  const isAuthenticated = !!user;

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUserPoints,
    isAuthenticated
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
